import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SvgIcon } from "@cinch-labs/design-system";
import { Actions, Context } from "@cinch-nx/data-search";
import { sendDigitalDataEvent } from "@cinch-nx/shared-analytics";
import { HoldingImage } from "@cinch-nx/shared-ui";
import { datadogRum } from "@datadog/browser-rum";
import cx from "classnames";
import Image from "next/legacy/image";
import { useEffect, useRef, useState } from "react";
import SwiperCore, { A11y, Navigation, Pagination } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import styles from "./vehicle-image-carousel.module.css";
SwiperCore.use([
    A11y,
    Navigation,
    Pagination
]);
const handleNavigateButtonClicked = (direction)=>()=>{
        sendDigitalDataEvent({
            event: {
                name: "click",
                category: "gallery navigate",
                label: "click",
                action: direction
            }
        });
        datadogRum.addAction(Actions.CLICK_MULTIPLE_NAV_IMAGE_BUTTON, {
            service: Context.SEARCH_MULTIPLE_IMAGES,
            squad: Context.SQUAD,
            direction
        });
    };
const handleCarouselImageViewed = (images, index)=>{
    const imageTitle = images[index].title;
    sendDigitalDataEvent({
        event: {
            name: "load",
            category: "thumbnail gallery",
            label: imageTitle,
            action: index + 1
        }
    });
    datadogRum.addAction(Actions.VIEWED_MULTIPLE_VEHICLE_CARD_IMAGE, {
        service: Context.SEARCH_MULTIPLE_IMAGES,
        squad: Context.SQUAD,
        label: imageTitle,
        imageIndex: index
    });
};
const handleCarouselImageClicked = (images, index)=>{
    const imageTitle = images[index].title;
    datadogRum.addAction(Actions.CLICK_MULTIPLE_VEHICLE_CARD_IMAGE, {
        service: Context.SEARCH_MULTIPLE_IMAGES,
        squad: Context.SQUAD,
        label: imageTitle,
        imageIndex: index
    });
};
export function VehicleImageCarousel({ images , isPending , isAvailable , productDetailUrl , ctaTitle , imageWithMorePhotosCta: imageTitleWithMorePhotosCta , imagesToDisplay: imageTitlesFilter  }) {
    const prevRef = useRef(null);
    const nextRef = useRef(null);
    const paginationRef = useRef(null);
    const [isFirstImageLoaded, setIsFirstImageLoaded] = useState(false);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const imagesToShow = imageTitlesFilter ? images.filter((image)=>imageTitlesFilter.includes(image.title)) : images;
    useEffect(()=>{
        setIsInitialLoad(false);
    }, []);
    const renderCarouselItem = ({ url , title  }, index)=>/*#__PURE__*/ _jsx(SwiperSlide, {
            "aria-roledescription": "slide",
            tag: "div",
            className: styles.carouselSlide,
            children: /*#__PURE__*/ _jsxs("a", {
                "data-testid": `pdp-url-${index}`,
                href: productDetailUrl,
                "aria-label": `${ctaTitle} ${title}`,
                onClick: ()=>handleCarouselImageClicked(images, index),
                children: [
                    !isFirstImageLoaded ? /*#__PURE__*/ _jsx(HoldingImage, {
                        isAvailable: isAvailable,
                        className: styles.holdingImage
                    }) : null,
                    /*#__PURE__*/ _jsxs("div", {
                        className: styles.carouselImageWrapper,
                        children: [
                            /*#__PURE__*/ _jsx(Image, {
                                src: url,
                                width: 391,
                                height: 220,
                                alt: title,
                                className: cx(styles.carouselImage, !isFirstImageLoaded && styles.hidden),
                                onLoadingComplete: index === 0 ? ()=>setIsFirstImageLoaded(true) : undefined
                            }),
                            title === imageTitleWithMorePhotosCta ? /*#__PURE__*/ _jsx("div", {
                                className: cx(styles.morePhotosBackground, !isFirstImageLoaded && styles.hidden),
                                "aria-hidden": "true",
                                children: /*#__PURE__*/ _jsx("div", {
                                    className: styles.morePhotosCTA,
                                    children: "More photos"
                                })
                            }) : null
                        ]
                    })
                ]
            })
        }, url);
    return /*#__PURE__*/ _jsx("div", {
        className: styles.vehicleImageCarouselContainer,
        children: isPending ? /*#__PURE__*/ _jsx(HoldingImage, {}) : /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(Swiper, {
                    className: styles.vehicleImageCarousel,
                    draggable: true,
                    wrapperTag: "div",
                    onSlideChange: (swiper)=>{
                        swiper.slides.forEach((slide, slideIndex)=>{
                            const isActiveSlide = slideIndex === swiper.activeIndex;
                            const ariaHidden = isActiveSlide ? "false" : "true";
                            slide.setAttribute("aria-hidden", ariaHidden);
                            const slideLink = slide.querySelector("a");
                            if (isActiveSlide) {
                                slideLink === null || slideLink === void 0 ? void 0 : slideLink.removeAttribute("tabindex");
                            } else {
                                slideLink === null || slideLink === void 0 ? void 0 : slideLink.setAttribute("tabindex", "-1");
                            }
                        });
                    },
                    onSlideChangeTransitionEnd: (swiper)=>{
                        if (!isInitialLoad) {
                            handleCarouselImageViewed(images, swiper.realIndex);
                        }
                    },
                    a11y: {
                        slideLabelMessage: "Image {{index}} of {{slidesLength}}"
                    },
                    navigation: {
                        nextEl: nextRef.current,
                        prevEl: prevRef.current
                    },
                    pagination: {
                        el: paginationRef.current,
                        bulletClass: styles.carouselBullet,
                        bulletActiveClass: styles.carouselBulletActive,
                        renderBullet: (index, className)=>`<li class="${className}">${index + 1}</li>`
                    },
                    loop: true,
                    "aria-label": "car images",
                    "aria-roledescription": "carousel",
                    "aria-atomic": "false",
                    "aria-live": "polite",
                    children: imagesToShow.map((image, index)=>renderCarouselItem(image, index))
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: cx(styles.carouselNav, !isFirstImageLoaded && styles.hidden),
                    children: [
                        /*#__PURE__*/ _jsx("button", {
                            type: "button",
                            className: styles.carouselNavButton,
                            ref: prevRef,
                            "aria-label": "Previous image",
                            onClick: handleNavigateButtonClicked("backward"),
                            children: /*#__PURE__*/ _jsx(SvgIcon, {
                                name: "chevron-left",
                                color: "pureWhite",
                                size: 24,
                                className: styles.carouselNavIcon
                            })
                        }),
                        /*#__PURE__*/ _jsx("ul", {
                            className: styles.paginationIndicators,
                            "aria-hidden": "true",
                            ref: paginationRef
                        }),
                        /*#__PURE__*/ _jsx("button", {
                            type: "button",
                            className: styles.carouselNavButton,
                            ref: nextRef,
                            "aria-label": "Next image",
                            onClick: handleNavigateButtonClicked("forward"),
                            children: /*#__PURE__*/ _jsx(SvgIcon, {
                                name: "chevron-right",
                                color: "pureWhite",
                                size: 24,
                                className: styles.carouselNavIcon
                            })
                        })
                    ]
                })
            ]
        })
    });
}
